import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Constrants from '../constrants/constrant';
import { useToast } from '../context/ToastProvider';
import './MyApps.css';

function MyApps() {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, purchased, downloaded
    const { addToast } = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        fetchMyApps();
    }, []);

    const fetchMyApps = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            const response = await Constrants.get('/my-apps', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data) {
                setApps(response.data.apps || []);
            }
        } catch (error) {
            console.error('Error fetching apps:', error);
            if (error.response?.status === 401) {
                navigate('/login');
            } else {
                addToast('Failed to load your apps', 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const filteredApps = apps.filter(app => {
        if (filter === 'all') return true;
        return app.type === filter;
    });

    const handleDownload = (app) => {
        navigate(`/app/details/${app.slug}`);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="my-apps-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading your apps...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>My Apps - App Store</title>
                <meta name="description" content="Manage your downloaded and purchased apps" />
            </Helmet>

            <div className="my-apps-container">
                <div className="my-apps-header">
                    <h1>My Apps</h1>
                    <p className="subtitle">Manage your downloaded and purchased applications</p>
                </div>

                <div className="apps-stats">
                    <div className="stat-card">
                        <div className="stat-number">{apps.length}</div>
                        <div className="stat-label">Total Apps</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{apps.filter(a => a.type === 'purchased').length}</div>
                        <div className="stat-label">Purchased</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{apps.filter(a => a.type === 'downloaded').length}</div>
                        <div className="stat-label">Downloaded</div>
                    </div>
                </div>

                <div className="filter-tabs">
                    <button
                        className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        All Apps ({apps.length})
                    </button>
                    <button
                        className={`filter-tab ${filter === 'purchased' ? 'active' : ''}`}
                        onClick={() => setFilter('purchased')}
                    >
                        Purchased ({apps.filter(a => a.type === 'purchased').length})
                    </button>
                    <button
                        className={`filter-tab ${filter === 'downloaded' ? 'active' : ''}`}
                        onClick={() => setFilter('downloaded')}
                    >
                        Free Apps ({apps.filter(a => a.type === 'downloaded').length})
                    </button>
                </div>

                {filteredApps.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">📱</div>
                        <h2>No apps found</h2>
                        <p>
                            {filter === 'all'
                                ? "You haven't downloaded or purchased any apps yet."
                                : filter === 'purchased'
                                    ? "You haven't purchased any apps yet."
                                    : "You haven't downloaded any free apps yet."}
                        </p>
                        <Link to="/" className="browse-btn">Browse Apps</Link>
                    </div>
                ) : (
                    <div className="apps-grid">
                        {filteredApps.map(app => (
                            <div key={app.id} className="app-card">
                                <div className="app-card-header">
                                    <img
                                        src={app.icon_url || '/placeholder-icon.png'}
                                        alt={app.name}
                                        className="app-icon"
                                        onError={(e) => e.target.src = '/placeholder-icon.png'}
                                    />
                                    <div className="app-info">
                                        <h3 className="app-name">{app.name}</h3>
                                        <p className="app-developer">{app.developer}</p>
                                        <div className="app-meta">
                                            <span className="app-category">{app.category}</span>
                                            {app.rating && parseFloat(app.rating) > 0 && (
                                                <span className="app-rating">
                                                    ⭐ {parseFloat(app.rating).toFixed(1)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="app-card-body">
                                    <div className="app-details">
                                        <div className="detail-item">
                                            <span className="detail-label">Version:</span>
                                            <span className="detail-value">{app.version || 'N/A'}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">
                                                {app.type === 'purchased' ? 'Purchased:' : 'Downloaded:'}
                                            </span>
                                            <span className="detail-value">
                                                {formatDate(app.purchased_at || app.downloaded_at)}
                                            </span>
                                        </div>
                                        {app.type === 'purchased' && (
                                            <div className="detail-item">
                                                <span className="detail-label">Price:</span>
                                                <span className="detail-value price">${app.price}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="app-badge">
                                        {app.type === 'purchased' ? (
                                            <span className="badge purchased">Purchased</span>
                                        ) : (
                                            <span className="badge free">Free</span>
                                        )}
                                    </div>
                                </div>

                                <div className="app-card-footer">
                                    <button
                                        className="btn-install"
                                        onClick={() => handleDownload(app)}
                                    >
                                        <span className="material-icons">download</span>
                                        Install
                                    </button>
                                    <Link
                                        to={`/app/details/${app.slug}`}
                                        className="btn-details"
                                    >
                                        View Details
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}

export default MyApps;
